/**
 * Places API (New) — REST helpers for address autocomplete.
 * Requires VITE_GOOGLE_MAPS_API_KEY with Places API (New) enabled.
 */

const AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";

function getApiKey() {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    throw new Error("Missing VITE_GOOGLE_MAPS_API_KEY. Add it to your .env file.");
  }
  return apiKey;
}

export function createSessionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {string} input
 * @param {string} [sessionToken]
 * @returns {Promise<Array<{ placeId: string, mainText: string, secondaryText: string, fullText: string }>>}
 */
export async function fetchPlaceSuggestions(input, sessionToken) {
  const query = String(input || "").trim();
  if (!query) return [];

  const body = {
    input: query,
    includedPrimaryTypes: ["street_address", "premise", "subpremise", "route"],
  };
  if (sessionToken) {
    body.sessionToken = sessionToken;
  }

  const response = await fetch(AUTOCOMPLETE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": getApiKey(),
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      data?.error?.message ||
      data?.error_message ||
      `Places autocomplete failed (${response.status})`;
    throw new Error(message);
  }

  return (data.suggestions || [])
    .map((item) => {
      const prediction = item.placePrediction;
      if (!prediction) return null;

      // placeId may be "places/ChIJ..." or just the id
      const rawId = prediction.placeId || prediction.place || "";
      const placeId = String(rawId).replace(/^places\//, "");

      const mainText =
        prediction.structuredFormat?.mainText?.text ||
        prediction.text?.text ||
        "";
      const secondaryText = prediction.structuredFormat?.secondaryText?.text || "";
      const fullText = prediction.text?.text || [mainText, secondaryText].filter(Boolean).join(", ");

      if (!placeId || !fullText) return null;

      return {
        placeId,
        mainText: mainText || fullText,
        secondaryText,
        fullText,
      };
    })
    .filter(Boolean);
}

/**
 * @param {string} placeId
 * @param {string} [sessionToken]
 */
export async function fetchPlaceDetails(placeId, sessionToken) {
  const id = String(placeId || "").replace(/^places\//, "");
  if (!id) {
    throw new Error("Missing place id");
  }

  const params = new URLSearchParams();
  if (sessionToken) {
    params.set("sessionToken", sessionToken);
  }
  const qs = params.toString();
  const url = `https://places.googleapis.com/v1/places/${encodeURIComponent(id)}${
    qs ? `?${qs}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": getApiKey(),
      "X-Goog-FieldMask": "id,displayName,formattedAddress,addressComponents",
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      data?.error?.message ||
      data?.error_message ||
      `Place details failed (${response.status})`;
    throw new Error(message);
  }

  return data;
}

/**
 * Parse Places API (New) addressComponents into form fields.
 */
export function parsePlaceAddressComponents(addressComponents = []) {
  const get = (type, useShort = false) => {
    const match = addressComponents.find((c) => c.types?.includes(type));
    if (!match) return "";
    if (useShort) {
      return match.shortText || match.short_name || match.longText || match.long_name || "";
    }
    return match.longText || match.long_name || match.shortText || match.short_name || "";
  };

  const streetNumber = get("street_number");
  const route = get("route");
  const address1 = [streetNumber, route].filter(Boolean).join(" ").trim();

  const city =
    get("locality") ||
    get("postal_town") ||
    get("sublocality_level_1") ||
    get("sublocality") ||
    get("administrative_area_level_2");

  const state =
    get("administrative_area_level_1", true) || get("administrative_area_level_1");
  const zip = get("postal_code");
  const country = get("country", true);

  return {
    address1,
    city,
    state,
    zip,
    country,
  };
}
